import { computed, shallowRef, type ComputedRef, type InjectionKey, type Ref } from 'vue';

/**
 * The trail of a list's record pane. A list shows its selected row in the
 * pane. Clicking a related record inside the pane moves the pane to that
 * record, and the trail remembers how you got there, such as "Angelica
 * Waelchi III › Main St Café". The selected row is the trail's start and is
 * not a step: with no steps the pane shows the row.
 *
 * Selecting another row starts the pane fresh, so the trail belongs to one
 * row at a time ({@see startFreshFor}).
 *
 * Spec: docs/specs/record-navigation-layers.md, "The pane's trail".
 */
export interface PaneTrailStep {
    entityId: string;
    recordId: number | string;
    /** "Location #12" until the record has loaded, then its name. */
    title: string;
    /** The section the record's inspector should open on, when a link asked for one. */
    initialSection?: string;
}

export interface PaneTrail {
    /** The records moved to, after the selected row. Empty while the pane shows the row. */
    readonly steps: Readonly<Ref<readonly PaneTrailStep[]>>;
    /** The record shown, or null while the pane shows the selected row. */
    readonly current: ComputedRef<PaneTrailStep | null>;
    /** Moves the pane on to a record. A record already shown is not added twice. */
    push(step: PaneTrailStep): void;
    /** Steps back one record. Returns false when the pane already shows the row. */
    back(): boolean;
    /** Goes back to the step at `index`, dropping the steps after it. -1 is the selected row. */
    goTo(index: number): void;
    /** Empties the trail, so the pane shows the selected row. */
    clear(): void;
    /**
     * Starts the trail fresh when the selected row changes. A call with the row
     * the trail already belongs to keeps the trail.
     */
    startFreshFor(rowKey: string | null): void;
    /** Names a record's steps once it has loaded. */
    rename(entityId: string, recordId: number | string, title: string): void;
}

export function createPaneTrail(): PaneTrail {
    const steps = shallowRef<PaneTrailStep[]>([]);
    let rowKey: string | null = null;

    const current = computed(() => (steps.value.length > 0 ? steps.value[steps.value.length - 1] : null));

    function isSameRecord(first: PaneTrailStep, entityId: string, recordId: number | string): boolean {
        return first.entityId === entityId && String(first.recordId) === String(recordId);
    }

    return {
        steps,
        current,
        push(step) {
            const shown = current.value;
            if (shown && isSameRecord(shown, step.entityId, step.recordId)) return;
            steps.value = [...steps.value, step];
        },
        back() {
            if (steps.value.length === 0) return false;
            steps.value = steps.value.slice(0, -1);
            return true;
        },
        goTo(index) {
            if (index < -1 || index >= steps.value.length - 1) return;
            steps.value = steps.value.slice(0, index + 1);
        },
        clear() {
            if (steps.value.length > 0) steps.value = [];
        },
        startFreshFor(nextRowKey) {
            if (nextRowKey === rowKey) return;
            rowKey = nextRowKey;
            if (steps.value.length > 0) steps.value = [];
        },
        rename(entityId, recordId, title) {
            const trimmed = title.trim();
            if (trimmed === '') return;
            if (!steps.value.some((step) => isSameRecord(step, entityId, recordId) && step.title !== trimmed)) return;
            steps.value = steps.value.map((step) => (isSameRecord(step, entityId, recordId) ? { ...step, title: trimmed } : step));
        },
    };
}

/**
 * Where a related record's link sends the record, when it sits in a list's
 * pane or in one of the list's rows. RecordPane provides one for the links
 * inside it. ListRowPaneScope provides one for the links in a table row or a
 * card, which selects that row first. A link with none of these opens the
 * side peek.
 */
export interface RecordPaneNavigator {
    /** False when the list has no pane to show the record in, such as on a phone. */
    isActive(): boolean;
    showRecord(entityId: string, recordId: number | string, options?: { initialSection?: string }): void;
    /** Names a record once it has loaded, for the trail. */
    renameRecord?(entityId: string, recordId: number | string, title: string): void;
}

export const RECORD_PANE_NAVIGATOR: InjectionKey<RecordPaneNavigator> = Symbol('record-pane-navigator');

/**
 * The list page's pane, as the rows reach it. A row's scope uses it to select
 * its row and move the pane on from there.
 */
export interface ListPaneHost {
    isActive(): boolean;
    showFromRow(rowId: string, entityId: string, recordId: number | string, options?: { initialSection?: string }): void;
}

export const LIST_PANE_HOST: InjectionKey<ListPaneHost> = Symbol('list-pane-host');
